// Mock API responses for local development mode.
// Keys are lowercase URL substrings; the interceptor picks the first match.

import { mockRecordingContent, mockSessionRecordings, SSH_POLICY_DYNAMIC_MOCKS, SSH_PROXY_MOCKS } from "./sshProxyMocks";
import { AUTH_LOG_MOCKS } from "./authLogsMocks";
import { SETTINGS_MOCKS } from "./settingsMocks";
import { SUDO_MOCKS } from "./sudoMocks";
import { PASSWORD_MOCKS } from "./passwordMocks";
import { DASHBOARD_MOCKS } from "./dashboardMocks";
import { SSO_MOCKS } from "./ssoMocks";
import { AUTHENTICATOR_MOCKS } from "./authenticatorMocks";
import { MANAGED_ACCOUNTS_MOCKS } from "./managedAccountsMocks";
import { ADMIN_MOCKS } from "./adminMocks";
import { LOCAL_USERS_MOCKS } from "./localUsersMocks";
import { CONNECT_MOCKS } from "./connectMocks";
import { DECOMMISSION_MOCKS, mockCleanupFiles, mockPasswordsKept } from "./decommissionMocks";
import { IDP_MOCKS } from "./idpMocks";
import { RADIUS_MOCKS } from "./radiusMocks";
import { RADIUS_LOGS_MOCKS } from "./radiusLogsMocks";

const MOCK_USER = {
  userId: 1,
  userName: "localdev@authnull.com",
  email: "localdev@authnull.com",
  firstName: "Local",
  lastName: "Dev",
  userRole: "ADMIN",
  orgId: 1,
  domainId: 1,
  status: "ACTIVE",
};

const MOCK_TENANT = {
  tenantId: 1,
  orgId: 1,
  tenantName: "Local Dev Tenant",
  domainUrl: "default.devsetup.dev.authnull.com",
  status: "ACTIVE",
};

const MOCK_PLATFORM_USERS = [
  {
    userId: 1, email: "guptasatyam1807@gmail.com", firstName: "john", lastName: "doe",
    role: "ADMIN", roleId: 1, status: "active", tenantId: 1, firstLogin: 1,
    mfaEnrolled: ["TOTP"],
  },
  {
    userId: 2, email: "saffran@authnull.com", firstName: "", lastName: "",
    role: "ADMIN", roleId: 1, status: "active", tenantId: 1, firstLogin: 1,
    mfaEnrolled: ["Passkey"],
  },
  {
    userId: 3, email: "sumit@authnull.com", firstName: "", lastName: "",
    role: "ADMIN", roleId: 1, status: "active", tenantId: 1, firstLogin: 1,
    mfaEnrolled: ["Passkey"],
  },
  {
    userId: 4, email: "aditya@authnull.com", firstName: "", lastName: "",
    role: "ADMIN", roleId: 1, status: "active", tenantId: 1, firstLogin: 1,
    mfaEnrolled: [],
  },
];

export const MOCK_RESPONSES = {
  // SSH Proxy — spread FIRST so its specific paths win. First match wins in
  // insertion order, and the generic "/policy" and "/endpoint" keys further
  // down would otherwise swallow "/policy/json/listpolicy" and
  // "/endpointusers/listallepmusers".
  ...SSH_PROXY_MOCKS,

  // User / session
  "/getuserdetails": { code: 200, data: MOCK_USER },
  "/userdetail": { code: 200, data: MOCK_USER },
  "/user-details": { code: 200, data: MOCK_USER },
  "/getuser": { code: 200, data: MOCK_USER },
  "/profile": { code: 200, data: MOCK_USER },

  // Tenant / auth
  // Kept above "/tenant": deployments that fold "/api/v1/tenants" into the base
  // URL would otherwise match that shorter key first (first match wins).
  "/listplatformusers": {
    code: 200,
    status: "success",
    counts: { total: 4, active: 4, inactive: 0, pending: 0 },
    page: 1,
    pageSize: 25,
    data: MOCK_PLATFORM_USERS,
  },
  "/getauthmethod": { code: 200, data: { authMethod: "NORMAL" } },
  "/gettenantdetail": { code: 200, data: MOCK_TENANT },
  "/tenantdetail": { code: 200, data: MOCK_TENANT },
  "/tenantlist": { code: 200, data: [MOCK_TENANT] },
  "/tenant": { code: 200, data: MOCK_TENANT },

  // Dashboard / analytics
  "/dashboard": { code: 200, data: { totalUsers: 12, totalEndpoints: 5, totalPolicies: 3, authLogs: [] } },
  "/getdashboard": { code: 200, data: { totalUsers: 12, totalEndpoints: 5, totalPolicies: 3 } },
  "/transaction": { code: 200, data: [], total: 0 },
  "/auth-log": { code: 200, data: [], total: 0 },
  "/authlog": { code: 200, data: [], total: 0 },
  "/auditlog": { code: 200, data: [], total: 0 },
  "/audit-log": { code: 200, data: [], total: 0 },

  // Directory / users
  "/directory/user": { code: 200, data: [], total: 0 },
  "/ad-user": { code: 200, data: [], total: 0 },
  "/aduser": { code: 200, data: [], total: 0 },
  "/group": { code: 200, data: [], total: 0 },
  "/walletuser": { code: 200, data: [], total: 0 },
  "/wallet-user": { code: 200, data: [], total: 0 },
  "/did": { code: 200, data: [], total: 0 },

  // PAM
  "/endpoint": { code: 200, data: [], total: 0 },
  "/pam/user": { code: 200, data: [], total: 0 },
  "/pam/policy": { code: 200, data: [], total: 0 },
  "/pam/credential": { code: 200, data: [], total: 0 },
  "/database": { code: 200, data: [], total: 0 },
  "/secret": { code: 200, data: [], total: 0 },
  "/radius": { code: 200, data: [], total: 0 },

  // Windows
  "/windows/endpoint": { code: 200, data: [], total: 0 },
  "/windows/policy": { code: 200, data: [], total: 0 },
  "/jit": { code: 200, data: [], total: 0 },

  // Policies
  "/policy": { code: 200, data: [], total: 0 },
  "/authentication-policy": { code: 200, data: [], total: 0 },

  // Settings / onboarding
  "/setting": { code: 200, data: {} },
  "/onboard": { code: 200, data: {} },
  "/setup": { code: 200, data: {} },

  // Workload / service accounts
  "/workload": { code: 200, data: [], total: 0 },
  "/service-account": { code: 200, data: [], total: 0 },

  // Agent / AI
  "/agent": { code: 200, data: [] },
  "/ai": { code: 200, data: {} },

  // Diagnostics
  "/diagnostic": { code: 200, data: {} },
  "/status": { code: 200, data: { status: "ok" } },
};

// Generic fallback returned when no pattern matches
export const GENERIC_SUCCESS = { code: 200, data: [], message: "OK (local mock)" };

// Some mocks depend on what was asked for, not just where. `body` is the raw
// request payload the interceptor saw, so those can answer per-id.
// Endpoint accounts, keyed by instanceId. Above the static table because the
// static "/instances/list" key would otherwise swallow "/instances/listaccounts".
const MOCK_ENDPOINT_ACCOUNTS = {
  1: [
    { name: "ubuntu", source: "gateway" },
    { name: "deploy", source: "manual" },
  ],
  2: [{ name: "oracle", source: "gateway" }],
};

// SSH grants: one key per policy, account and endpoint. Policy …0001 is the
// web-01 ubuntu policy; …0002 holds sshtest on web-01, so a new policy for
// sshtest there triggers the "held by another policy" warning.
const MOCK_GRANTS = [
  { id: "g-1", policyId: "7f1c2d9e-0000-4000-8000-000000000001", policyName: "web admins", endpointId: 1, account: "ubuntu", jumpServerId: 1, status: "active", keyFingerprint: "SHA256:Qm4vK9xT2bLw8sYp3fNd0cRj7eHu5aZg1iWo6kMtVnE", createdAt: "2026-10-05T03:10:00Z", activatedAt: "2026-10-05T03:10:06Z" },
  { id: "g-2", policyId: "7f1c2d9e-0000-4000-8000-000000000001", endpointId: 1, account: "deploy", jumpServerId: 1, status: "pending", createdAt: "2026-10-05T03:12:00Z" },
  { id: "g-3", policyId: "7f1c2d9e-0000-4000-8000-000000000001", endpointId: 2, account: "ubuntu", jumpServerId: 1, status: "failed", createdAt: "2026-10-05T03:12:00Z" },
  { id: "g-4", policyId: "7f1c2d9e-0000-4000-8000-000000000001", endpointId: 1, account: "oracle", jumpServerId: 1, status: "disabled", keyFingerprint: "SHA256:Ol9dK3yEnd3dF1ngErPr1nTxQ2wE4rT6yU8iO0pA1sD", disabledReason: "policy changed", createdAt: "2026-10-04T09:00:00Z", disabledAt: "2026-10-05T03:12:00Z" },
  { id: "g-5", policyId: "7f1c2d9e-0000-4000-8000-000000000002", endpointId: 1, account: "sshtest", jumpServerId: 1, status: "active", keyFingerprint: "SHA256:Zx1cV2bN3mA4sD5fG6hJ7kL8qW9eR0tY1uI2oP3aS4d", createdAt: "2026-10-03T11:00:00Z", activatedAt: "2026-10-03T11:00:05Z" },
];

// Account scans: the first status check reports "waiting for the setup
// script" (a handshake failure); later checks report done.
const MOCK_SCAN_POLLS = {};

// Enrolment tokens: gateway 1 can build the command; gateway 3 ("lab-jump")
// has not reported its host key, so create returns commandUnavailable.
const MOCK_ENROL_TOKENS = [
  { id: "tok-1", jumpServerId: 1, name: "web fleet", expiresAt: new Date(Date.now() + 20 * 3600e3).toISOString(), maxUses: 100, uses: 12, createdAt: new Date(Date.now() - 4 * 3600e3).toISOString() },
  { id: "tok-2", jumpServerId: 1, name: "old batch", expiresAt: new Date(Date.now() - 3600e3).toISOString(), maxUses: 10, uses: 10, createdAt: new Date(Date.now() - 50 * 3600e3).toISOString(), revokedAt: new Date(Date.now() - 2 * 3600e3).toISOString() },
];

const DYNAMIC_MOCKS = [
  // Decommission / recommission change the shared MOCK_INSTANCES rows, so the
  // list and the detail page follow. The clean-up job's progress is kept on the
  // row (_mockDecom): it finishes on the third poll. web-02.stage (5) starts
  // Decommissioning and its clean-up fails, to show Force.
  {
    pattern: "/instances/decommission",
    respond: (body) => {
      const row = SSH_PROXY_MOCKS["/instances/list"].instances.find((r) => r.instanceId === Number(body?.instanceId));
      if (row?.status === "Decommissioned") return { status: "decommissioned", message: "already decommissioned" };
      if (body?.force || !row?.jumpServerId) {
        if (row) Object.assign(row, { status: "Decommissioned", _mockDecom: { forced: true } });
        return {
          status: "decommissioned", grantsEnded: 2, machineCleaned: false,
          passwordsKept: mockPasswordsKept(body?.instanceId), cleanupFiles: mockCleanupFiles(body?.instanceId),
          message: "Authnull's records are cleared. The machine itself was not cleaned up: if it still exists, remove the files in cleanupFiles by hand.",
        };
      }
      Object.assign(row, { status: "Decommissioning", _mockDecom: { polls: 0, jobId: `decom-${row.instanceId}-${Date.now()}` } });
      return { status: "decommissioning", jobId: row._mockDecom.jobId, grantsEnded: 2, passwordsKept: mockPasswordsKept(row.instanceId) };
    },
  },
  {
    pattern: "/instances/recommission",
    respond: (body) => {
      const id = Number(body?.instanceId);
      const row = SSH_PROXY_MOCKS["/instances/list"].instances.find((r) => r.instanceId === id);
      if (row) Object.assign(row, { status: "Active", _mockDecom: undefined });
      MOCK_SCAN_POLLS[id] = 0;
      return {
        status: "Active",
        instanceId: id,
        account: "authnull-svc",
        keyFingerprint: "SHA256:r3C0mm1ss10nK3yF1ngErPr1nT000000000000000",
        publicKey: "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIMockRecommissionKey authnull-svc",
        script:
          "#!/bin/sh\n# Authnull endpoint setup (mock, recommission)\nset -eu\nid authnull-svc >/dev/null 2>&1 || useradd -r -m -s /bin/sh authnull-svc\ninstall -d -m 0755 /etc/ssh/authnull/keys\necho 'AuthorizedKeysFile .ssh/authorized_keys /etc/ssh/authnull/keys/%u' > /etc/ssh/sshd_config.d/50-authnull.conf\nsshd -t && systemctl reload ssh\n",
        ...(row?.jumpServerId ? { scanJobId: `scan-job-${id}` } : {}),
      };
    },
  },
  {
    pattern: "/instances/jobstatus",
    respond: (body) => {
      if (body?.kind !== "decommission") return { status: "never" };
      const id = Number(body?.instanceId);
      const row = SSH_PROXY_MOCKS["/instances/list"].instances.find((r) => r.instanceId === id);
      const st = row?._mockDecom;
      const queuedAt = new Date(Date.now() - 60e3).toISOString();
      if (st?.forced) return { status: "never" };
      if (st) {
        st.polls += 1;
        if (st.polls < 3) return { jobId: st.jobId, status: st.polls < 2 ? "queued" : "claimed", attempts: st.polls - 1, queuedAt };
        row.status = "Decommissioned";
        return { jobId: st.jobId, status: "done", attempts: 2, queuedAt, completedAt: new Date().toISOString() };
      }
      if (id === 4) {
        return { jobId: "decom-1", status: "done", attempts: 1, queuedAt: "2026-09-28T10:00:00Z", completedAt: "2026-09-28T10:01:30Z" };
      }
      if (id === 5) {
        return { jobId: "decom-5", status: "failed", attempts: 20, queuedAt: "2026-10-06T08:00:00Z", completedAt: "2026-10-07T08:00:00Z", error: "ssh: handshake failed: ssh: unable to authenticate" };
      }
      return { status: "never" };
    },
  },
  {
    pattern: "/instances/enroltokens/create",
    respond: (body) => {
      const t = {
        id: `tok-${MOCK_ENROL_TOKENS.length + 1}`,
        jumpServerId: Number(body?.jumpServerId),
        name: body?.name || "",
        expiresAt: new Date(Date.now() + (Number(body?.ttlHours) || 24) * 3600e3).toISOString(),
        maxUses: Number(body?.maxUses) || 100,
        uses: 0,
        createdAt: new Date().toISOString(),
      };
      MOCK_ENROL_TOKENS.unshift(t);
      const base = { token: "enrol_mock_8f3c1a", tokenInfo: t };
      return Number(body?.jumpServerId) === 3
        ? { ...base, commandUnavailable: "the gateway has not reported its host key yet; is it running this release?" }
        : {
            ...base,
            command: `k=$(mktemp) && printf '%s\\n' '[203.0.113.10]:2222 ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIMockHostKey' > "$k" && ssh -o UserKnownHostsFile="$k" -o StrictHostKeyChecking=yes -o BatchMode=yes -p 2222 authnull-enrol@203.0.113.10 enrol enrol_mock_8f3c1a "$(hostname)" | sudo sh; rm -f "$k"`,
          };
    },
  },
  {
    pattern: "/instances/enroltokens/list",
    respond: (body) => ({
      tokens: MOCK_ENROL_TOKENS.filter((t) => !body?.jumpServerId || t.jumpServerId === Number(body.jumpServerId)),
    }),
  },
  {
    pattern: "/instances/enroltokens/revoke",
    respond: (body) => {
      const t = MOCK_ENROL_TOKENS.find((x) => x.id === body?.id);
      if (t) t.revokedAt = new Date().toISOString();
      return { status: "revoked" };
    },
  },
  {
    pattern: "/instances/reconcilesetup",
    respond: (body) => ({
      instanceId: Number(body?.instanceId),
      account: "authnull-svc",
      keyFingerprint: "SHA256:rC0nc1LeK3yF1ngErPr1nT0000000000000000000",
      publicKey: "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIMockReconcileKey authnull-svc",
      script:
        "#!/bin/sh\n# Authnull endpoint setup (mock)\nset -eu\nid authnull-svc >/dev/null 2>&1 || useradd -r -m -s /bin/sh authnull-svc\ninstall -d -m 0755 /etc/ssh/authnull/keys\necho 'AuthorizedKeysFile .ssh/authorized_keys /etc/ssh/authnull/keys/%u' > /etc/ssh/sshd_config.d/50-authnull.conf\nsshd -t && systemctl reload ssh\n",
      scanJobId: "scan-job-1",
    }),
  },
  {
    pattern: "/instances/scanstatus",
    respond: (body) => {
      const id = Number(body?.instanceId);
      MOCK_SCAN_POLLS[id] = (MOCK_SCAN_POLLS[id] || 0) + 1;
      if (MOCK_SCAN_POLLS[id] < 3) {
        return { jobId: "scan-job-1", status: "queued", attempts: MOCK_SCAN_POLLS[id], queuedAt: "2026-10-05T06:00:00Z", error: "ssh: handshake failed: ssh: unable to authenticate" };
      }
      return { jobId: "scan-job-1", status: "done", attempts: 3, queuedAt: "2026-10-05T06:00:00Z", completedAt: "2026-10-05T06:01:10Z" };
    },
  },
  {
    pattern: "/instances/scanaccounts",
    respond: () => ({ jobId: "scan-job-2", gatewayId: 1 }),
  },
  {
    pattern: "/instances/listgrants",
    respond: (body) => ({
      grants: MOCK_GRANTS.filter(
        (g) => (!body?.policyId || g.policyId === body.policyId) && (!body?.instanceId || g.endpointId === Number(body.instanceId)),
      ),
    }),
  },
  {
    pattern: "/instances/syncgrants",
    respond: () => ({ created: 1, kept: 1, disabled: 0, skipped: ["host 10.9.9.9", "account root", "endpoint 3 has no gateway"] }),
  },
  {
    pattern: "/instances/listaccounts",
    respond: (body) => ({
      endpoints: (body?.instanceIds || []).map((id) => ({ instanceId: id, accounts: MOCK_ENDPOINT_ACCOUNTS[id] || [] })),
    }),
  },
  {
    pattern: "/instances/setaccounts",
    respond: (body) => {
      const id = Number(body?.instanceId);
      const agent = (MOCK_ENDPOINT_ACCOUNTS[id] || []).filter((a) => a.source !== "manual");
      const names = new Set(agent.map((a) => a.name));
      const manual = (body?.accounts || []).filter((n) => !names.has(n)).map((name) => ({ name, source: "manual" }));
      MOCK_ENDPOINT_ACCOUNTS[id] = [...agent, ...manual];
      return { instanceId: id, accounts: MOCK_ENDPOINT_ACCOUNTS[id] };
    },
  },
  {
    pattern: "/sessionrecording/getrecordingcontent",
    respond: (body) => mockRecordingContent(Number(body?.id) || 9001),
  },
  {
    pattern: "/sessionrecording/listsessionrecording",
    respond: (body) => mockSessionRecordings(body),
  },
];

function parseBody(body) {
  if (!body) return null;
  if (typeof body === "object") return body;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

export function findMockResponse(url = "", body = null) {
  const lower = url.toLowerCase();

  for (const { pattern, respond } of [...RADIUS_MOCKS, ...IDP_MOCKS, ...DECOMMISSION_MOCKS, ...SSH_POLICY_DYNAMIC_MOCKS, ...ADMIN_MOCKS, ...LOCAL_USERS_MOCKS, ...CONNECT_MOCKS, ...PASSWORD_MOCKS, ...DASHBOARD_MOCKS, ...DYNAMIC_MOCKS, ...RADIUS_LOGS_MOCKS, ...AUTH_LOG_MOCKS, ...SETTINGS_MOCKS, ...SUDO_MOCKS, ...SSO_MOCKS, ...AUTHENTICATOR_MOCKS, ...MANAGED_ACCOUNTS_MOCKS]) {
    if (lower.includes(pattern)) return respond(parseBody(body));
  }

  for (const [pattern, response] of Object.entries(MOCK_RESPONSES)) {
    if (lower.includes(pattern)) return response;
  }
  return GENERIC_SUCCESS;
}
